import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, IconButton, TextField, Avatar,
  useTheme, alpha, Tooltip, Fade, Menu, MenuItem,
  CircularProgress, Popover, Paper,
} from '@mui/material';
import {
  Send as SendIcon,
  EmojiEmotions as EmojiIcon, AttachFile as AttachIcon,
  Reply as ReplyIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, InsertDriveFile as FileIcon,
  MoreVert as MoreIcon, Close as CloseIcon,
  Bookmark as BookmarkIcon,
  KeyboardArrowUp as ArrowUpIcon, KeyboardArrowDown as ArrowDownIcon,
  Done as DoneIcon, DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import {
  isDemoUser,
  DEMO_DM_ROOMS, DEMO_DM_MESSAGES, DEMO_ONLINE_USERS,
} from '../../utils/demoMode';

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• Types â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

interface ChatMessage {
  id: number;
  content: string;
  messageType: string;
  createdAt: string;
  isEdited?: boolean;
  isDeleted?: boolean;
  isPinned?: boolean;
  isPriority?: boolean;
  formatting?: string;
  pinnedBy?: string;
  pinnedAt?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  replyToId?: number;
  channelId?: number;
  roomId?: number;
  threadId?: number;
  replyCount?: number;
  lastReplyAt?: string;
  replyTo?: {
    id: number;
    content: string;
    sender: { id: string; email: string; firstName?: string; lastName?: string };
  };
  reactions?: { emoji: string; count: number; users: { id: string; email: string }[] }[];
  readBy?: { userId: string; email: string; readAt: string }[];
  isRead?: boolean;
  sender: { id: string; email: string; firstName?: string; lastName?: string; avatarUrl?: string };
  poll?: { id: number; question: string; options: { id: number; text: string; votes: number }[]; totalVotes: number };
}

interface OnlineUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  status: 'available' | 'busy' | 'away' | 'dnd' | 'brb' | 'offline' | 'invisible';
  statusMessage?: string;
  statusEmoji?: string;
  lastSeen: string;
  domain?: string;
}

interface DmRoom {
  id: number;
  otherUser: { id: string; email: string; firstName?: string; lastName?: string; avatarUrl?: string; domain?: string; status?: string };
  lastMessage?: { content: string; messageType: string; createdAt: string; senderId: string };
  unreadCount: number;
}

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• Helpers â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '😮', '😢'];

const EMOJI_LIST = ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','🥰','😍','🤩','😘','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','👋','🤚','✋','👌','✌️','🤞','🤟','🤘','🤙','👍','👎','✊','👊','👏','🙌','🤝','🙏','❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','💕','💯','✅','❌','🔥','⭐','✨','💫','🎉','🎊','🎁','🏆','⚡','💡','📌','🔔','🎵','☕','🍕','🍔','🍻'];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'available': case 'online': return '#10b981';
    case 'busy': return '#ef4444';
    case 'away': return '#f59e0b';
    case 'dnd': return '#dc2626';
    case 'brb': return '#8b5cf6';
    case 'invisible': case 'offline': return '#6b7280';
    default: return '#6b7280';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'available': return 'Available';
    case 'busy': return 'Busy';
    case 'away': return 'Away';
    case 'dnd': return 'Do not disturb';
    case 'brb': return 'Be right back';
    case 'invisible': return 'Appear offline';
    case 'offline': return 'Offline';
    default: return 'Unknown';
  }
};

const getAvatarGradient = (email: string) => {
  const colors = [
    'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
    'linear-gradient(135deg, #059669 0%, #047857 100%)',
    'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
    'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
    'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
    'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
  ];
  return colors[(email || '').charCodeAt(0) % colors.length];
};

const formatTime = (dateStr: string) => new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatRelativeTime = (dateStr: string) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const USER_NAME_COLORS = ['#6366f1','#0891b2','#059669','#2563eb','#7c3aed','#db2777','#ea580c','#0284c7'];
const getUserNameColor = (email: string) => USER_NAME_COLORS[(email || '').charCodeAt(0) % USER_NAME_COLORS.length];

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
};

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getUserDisplayName = (sender: { firstName?: string; lastName?: string; email?: string }) => {
  if (sender?.firstName || sender?.lastName) return `${sender.firstName || ''} ${sender.lastName || ''}`.trim();
  return sender?.email?.split('@')[0] || 'Unknown';
};

const getInitials = (sender: { firstName?: string; lastName?: string; email?: string }) => {
  if (sender?.firstName && sender?.lastName) return `${sender.firstName[0]}${sender.lastName[0]}`.toUpperCase();
  if (sender?.firstName) return sender.firstName.substring(0, 2).toUpperCase();
  return sender?.email?.substring(0, 2).toUpperCase() || '??';
};

const isSameDay = (d1: string, d2: string) => new Date(d1).toDateString() === new Date(d2).toDateString();

const isSameMinuteAndSender = (m1: ChatMessage, m2: ChatMessage) => {
  if (m1.sender.id !== m2.sender.id) return false;
  const t1 = new Date(m1.createdAt).getTime();
  const t2 = new Date(m2.createdAt).getTime();
  return Math.abs(t1 - t2) < 60000;
};

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• Component â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

const TeamChatPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { accentColor } = useThemeStore();
  const { token, user } = useAuthStore();
  const isDemo = isDemoUser(user?.email);

  const accentColors: Record<string, { main: string; light: string; dark: string }> = {
    blue:   { main: '#1a73e8', light: '#4285f4', dark: '#1557b0' },
    green:  { main: '#34a853', light: '#5bb974', dark: '#1e8e3e' },
    teal:   { main: '#009688', light: '#4db6ac', dark: '#00796b' },
    purple: { main: '#7c3aed', light: '#a78bfa', dark: '#6d28d9' },
    orange: { main: '#ea580c', light: '#fb923c', dark: '#c2410c' },
    red:    { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    pink:   { main: '#ec4899', light: '#f472b6', dark: '#db2777' },
    yellow: { main: '#eab308', light: '#facc15', dark: '#ca8a04' },
    gray:   { main: '#6b7280', light: '#9ca3af', dark: '#4b5563' },
  };
  const accent = accentColors[accentColor] || accentColors.blue;

  // â”€â”€â”€ DM State â”€â”€â”€
  const [dmRooms, setDmRooms] = useState<DmRoom[]>([]);
  const [activeDmRoom, setActiveDmRoom] = useState<DmRoom | null>(null);
  const [dmMessages, setDmMessages] = useState<ChatMessage[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  // All active users in the domain (includes offline users who have no socket presence)
  const [allDomainUsers, setAllDomainUsers] = useState<OnlineUser[]>([]);
  const [connected, setConnected] = useState(false);
  const [message, setMessage] = useState('');
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [hoverMsgId, setHoverMsgId] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; message: ChatMessage } | null>(null);
  const [dmTypingUsers, setDmTypingUsers] = useState<Array<{ roomId: number; email: string }>>([]);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [emojiTarget, setEmojiTarget] = useState<'input' | number | null>('input');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [pastedImageFile, setPastedImageFile] = useState<File | null>(null);
  const [pastedImagePreviewUrl, setPastedImagePreviewUrl] = useState<string | null>(null);

  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [currentUserStatus, setCurrentUserStatus] = useState<'available'|'busy'|'away'|'dnd'|'brb'|'invisible'>('available');
  const [statusMessage, setStatusMessage] = useState('');

  const [msgSearchOpen, setMsgSearchOpen] = useState(false);
  const [msgSearchQuery, setMsgSearchQuery] = useState('');
  const [msgSearchIdx, setMsgSearchIdx] = useState(0);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileUploadRef = useRef<((file: File) => Promise<void>) | null>(null);

  const activeDmRoomRef = useRef<DmRoom | null>(null);

  useEffect(() => { activeDmRoomRef.current = activeDmRoom; }, [activeDmRoom]);

  const currentUserId = user?.id || '';
  const apiUrl = import.meta.env.VITE_API_URL || '/api';
  const authHeaders = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const sidebarBg    = isDark ? '#1a1d27' : '#ffffff';
  const mainBg       = isDark ? '#0f1117' : '#ffffff';
  const borderColor  = isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
  const hoverBg      = isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6';
  const scrollbarThumb = isDark ? '#4a4d5a' : '#c1c8d4';
  const otherBubbleBg = isDark ? alpha('#fff', 0.09) : '#ffffff';
  const otherBubbleShadow = isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.08)';

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // â”€â”€â”€ API â”€â”€â”€
  const fetchDmRooms = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/chat/dm/rooms`, { headers: authHeaders });
      const json = await res.json();
      const data = json.data?.data || json.data || json;
      if (Array.isArray(data)) setDmRooms(data);
    } catch {}
  }, [apiUrl, authHeaders]);

  const fetchDmMessages = useCallback(async (roomId: number) => {
    try {
      const res = await fetch(`${apiUrl}/chat/dm/${roomId}/messages`, { headers: authHeaders });
      const json = await res.json();
      const data = json.data?.data || json.data || json;
      if (Array.isArray(data)) {
        setDmMessages(data);
        setTimeout(() => scrollToBottom(false), 100);
      }
    } catch {}
  }, [apiUrl, authHeaders, scrollToBottom]);

  // Fetches ALL active users in the domain — including those who are offline or
  // have never connected via socket. Socket events update presence on top of this list.
  const fetchAllDomainUsers = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/chat/all-users`, { headers: authHeaders });
      const json = await res.json();
      const data = json.data?.data || json.data || [];
      if (Array.isArray(data)) setAllDomainUsers(data);
    } catch {}
  }, [apiUrl, authHeaders]);

  // â”€â”€â”€ Socket â”€â”€â”€
  const connectSocket = useCallback(() => {
    if (!token || socketRef.current?.connected) return;
    socketRef.current = io(window.location.origin, {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current.on('connect', () => setConnected(true));
    socketRef.current.on('connected', () => { setConnected(true); fetchDmRooms(); });
    socketRef.current.on('disconnect', () => setConnected(false));
    socketRef.current.on('connect_error', () => setConnected(false));
    socketRef.current.on('onlineUsers', (users: OnlineUser[]) => setOnlineUsers(users));
    socketRef.current.on('userOnline', (data: { userId: string; email: string }) => {
      setOnlineUsers(prev => {
        if (prev.find(u => u.id === data.userId))
          return prev.map(u => u.id === data.userId ? { ...u, status: 'available' as const } : u);
        return [...prev, { id: data.userId, email: data.email, status: 'available' as const, lastSeen: new Date().toISOString() }];
      });
    });
    socketRef.current.on('userOffline', (data: { userId: string }) => {
      setOnlineUsers(prev => prev.map(u => u.id === data.userId ? { ...u, status: 'offline' as const } : u));
    });
    socketRef.current.on('userStatusChange', (data: { userId: string; status: string; statusMessage?: string }) => {
      setOnlineUsers(prev => prev.map(u => u.id === data.userId ? { ...u, status: data.status as any, statusMessage: data.statusMessage } : u));
    });
    socketRef.current.on('newDmMessage', (msg: ChatMessage) => {
      if (activeDmRoomRef.current && msg.roomId === activeDmRoomRef.current.id) {
        setDmMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
        setTimeout(scrollToBottom, 100);
      }
      fetchDmRooms();
    });
    socketRef.current.on('dmRoomCreated', () => fetchDmRooms());
    socketRef.current.on('dmUserTyping', (data: { roomId: number; email: string }) => {
      setDmTypingUsers(prev =>
        prev.some(t => t.roomId === data.roomId && t.email === data.email) ? prev : [...prev, data]
      );
      setTimeout(() =>
        setDmTypingUsers(prev => prev.filter(t => !(t.roomId === data.roomId && t.email === data.email))),
        4000
      );
    });
    socketRef.current.on('dmUserStopTyping', (data: { roomId: number; email: string }) => {
      setDmTypingUsers(prev => prev.filter(t => !(t.roomId === data.roomId && t.email === data.email)));
    });
    socketRef.current.on('reactionUpdate', (data: { messageId: number; reactions: any[] }) => {
      setDmMessages(prev => prev.map(m => m.id === data.messageId ? { ...m, reactions: data.reactions } : m));
    });
    socketRef.current.on('messageEdited', (msg: ChatMessage) => {
      setDmMessages(prev => prev.map(m => m.id === msg.id ? { ...m, ...msg } : m));
    });
    socketRef.current.on('messageDeleted', (data: { id: number }) => {
      setDmMessages(prev => prev.map(m => m.id === data.id ? { ...m, isDeleted: true, content: '' } : m));
    });
    socketRef.current.on('dmRead', (data: { roomId: number; userId: string }) => {
      // When the other person reads messages, mark all as read for sender view
      if (activeDmRoomRef.current && data.roomId === activeDmRoomRef.current.id) {
        setDmMessages(prev => prev.map(m => ({ ...m, isRead: true })));
      }
    });
  }, [token, fetchDmRooms, scrollToBottom]);

  useEffect(() => {
    if (isDemo) return;
    connectSocket();
    return () => { socketRef.current?.disconnect(); };
  }, [connectSocket, isDemo]);

  useEffect(() => {
    if (isDemo) return;
    if (token) fetchDmRooms();
  }, [token, fetchDmRooms, isDemo]);

  useEffect(() => {
    if (isDemo) return;
    if (token) fetchAllDomainUsers();
  }, [token, fetchAllDomainUsers, isDemo]);

  useEffect(() => {
    if (!isDemo) return;
    setDmRooms(DEMO_DM_ROOMS as any);
    setOnlineUsers(DEMO_ONLINE_USERS as any);
    setConnected(true);
  }, [isDemo]);

  // â”€â”€â”€ Handlers â”€â”€â”€
  const handleOpenDm = useCallback(async (userId: string) => {
    if (isDemo) {
      const room = DEMO_DM_ROOMS.find(r => r.otherUser.id === userId) || DEMO_DM_ROOMS[0];
      setActiveDmRoom(room as any);
      setDmMessages((DEMO_DM_MESSAGES[room.id] as any) || []);
      setTimeout(() => scrollToBottom(false), 100);
      return;
    }
    if (!socketRef.current) return;
    socketRef.current.emit('openDm', { otherUserId: userId }, (response: any) => {
      if (response?.success) {
        setActiveDmRoom(response.data.room);
        setDmMessages(response.data.messages || []);
        fetchDmRooms();
        setTimeout(() => scrollToBottom(false), 100);
        // Mark messages as read
        socketRef.current?.emit('markDmRead', { roomId: response.data.room.id });
      }
    });
  }, [fetchDmRooms, scrollToBottom, isDemo]);

  const handleSendMessage = useCallback(async () => {
    if (isDemo) return;
    if (pastedImageFile) {
      if (fileUploadRef.current) await fileUploadRef.current(pastedImageFile);
      setPastedImageFile(null);
      if (pastedImagePreviewUrl) { URL.revokeObjectURL(pastedImagePreviewUrl); setPastedImagePreviewUrl(null); }
      return;
    }
    const content = message.trim();
    if (!content || !socketRef.current || !activeDmRoom) return;
    if (editingMessage) {
      // Optimistic update so message changes immediately
      setDmMessages(prev => prev.map(m => m.id === editingMessage.id ? { ...m, content, isEdited: true } : m));
      socketRef.current.emit('editMessage', { messageId: editingMessage.id, content });
      setEditingMessage(null);
      setMessage('');
      return;
    }
    socketRef.current.emit('sendDm', { roomId: activeDmRoom.id, content, replyToId: replyTo?.id });
    setMessage('');
    setReplyTo(null);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socketRef.current.emit('dmStopTyping', { roomId: activeDmRoom.id });
  }, [message, pastedImageFile, pastedImagePreviewUrl, editingMessage, activeDmRoom, replyTo, isDemo]);

  const handleTyping = useCallback(() => {
    if (!activeDmRoom || !socketRef.current) return;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socketRef.current.emit('dmTyping', { roomId: activeDmRoom.id });
    typingTimeoutRef.current = setTimeout(() =>
      socketRef.current?.emit('dmStopTyping', { roomId: activeDmRoom.id }),
      2000
    );
  }, [activeDmRoom]);

  const handleReaction = useCallback((messageId: number, emoji: string) => {
    socketRef.current?.emit('toggleReaction', { messageId, emoji });
  }, []);

  const handleDeleteMsg = useCallback((messageId: number) => {
    socketRef.current?.emit('deleteMessage', { messageId });
  }, []);

  const handleFileUpload = useCallback(async (file: File) => {
    if (!file || !activeDmRoom) return;
    if (file.size > 25 * 1024 * 1024) { alert('File too large (max 25MB)'); return; }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('roomId', String(activeDmRoom.id));
      const res = await fetch(`${apiUrl}/chat/dm/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
    } catch { alert('Failed to upload file. Please try again.'); }
    finally { setUploadingFile(false); }
  }, [apiUrl, token, activeDmRoom]);

  useEffect(() => { fileUploadRef.current = handleFileUpload; }, [handleFileUpload]);

  const ingestClipboardImage = useCallback((cd: DataTransfer | null): boolean => {
    if (!cd) return false;
    let file: File | null = null;
    if (cd.items?.length) {
      const item = Array.from(cd.items).find(it => it.kind === 'file' && it.type.startsWith('image/'));
      if (item) file = item.getAsFile();
    }
    if (!file && cd.files?.length) {
      const f = Array.from(cd.files).find(f => f.type.startsWith('image/'));
      if (f) file = f;
    }
    if (!file) return false;
    const ext = (file.type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '') || 'png';
    const named = new File([file], `paste-${Date.now()}.${ext}`, { type: file.type });
    setPastedImageFile(named);
    setPastedImagePreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(named); });
    return true;
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (ingestClipboardImage(e.clipboardData)) e.preventDefault();
  }, [ingestClipboardImage]);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.isContentEditable || t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA') {
        if (t.closest('[data-composer-textarea="true"]')) return;
      }
      if (ingestClipboardImage(e.clipboardData)) e.preventDefault();
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [ingestClipboardImage]);



  const handleStatusChange = useCallback((newStatus: typeof currentUserStatus) => {
    setCurrentUserStatus(newStatus);
    socketRef.current?.emit('changeStatus', { status: newStatus, statusMessage });
    setStatusMenuAnchor(null);
  }, [statusMessage]);

  // â”€â”€â”€ Derived â”€â”€â”€
  const userDomain = useMemo(() => user?.email?.split('@')[1] || '', [user]);

  const teamUsersWithoutConversations = useMemo(() => {
    const dmUserIds = new Set(dmRooms.map(r => r.otherUser.id));
    // Build a map of socket-reported presence so we can overlay live status
    const onlineMap = new Map(onlineUsers.map(u => [u.id, u]));
    const query = userFilter.toLowerCase();
    return allDomainUsers
      .filter(u => u.id !== user?.id && !dmUserIds.has(u.id))
      // Overlay live socket presence on top of the API snapshot
      .map(u => ({ ...u, ...(onlineMap.get(u.id) || {}) }))
      .filter(u => !query || getUserDisplayName(u).toLowerCase().includes(query) || u.email?.toLowerCase().includes(query))
      .sort((a, b) => {
        if (a.status === 'available' && b.status !== 'available') return -1;
        if (a.status !== 'available' && b.status === 'available') return 1;
        return getUserDisplayName(a).localeCompare(getUserDisplayName(b));
      });
  }, [allDomainUsers, onlineUsers, user, userFilter, dmRooms]);

  const filteredDmRooms = useMemo(() => {
    if (!sidebarSearch.trim()) return dmRooms;
    const q = sidebarSearch.toLowerCase();
    return dmRooms.filter(r =>
      getUserDisplayName(r.otherUser).toLowerCase().includes(q) ||
      r.otherUser.email?.toLowerCase().includes(q)
    );
  }, [dmRooms, sidebarSearch]);

  const currentTyping = useMemo(() =>
    dmTypingUsers
      .filter(t => activeDmRoom && t.roomId === activeDmRoom.id)
      .map(t => t.email),
    [dmTypingUsers, activeDmRoom]
  );

  const otherUserInfo = useMemo(() =>
    activeDmRoom ? onlineUsers.find(u => u.id === activeDmRoom.otherUser.id) : null,
    [onlineUsers, activeDmRoom]
  );

  const msgSearchMatches = useMemo(() => {
    if (!msgSearchQuery.trim()) return [];
    const q = msgSearchQuery.toLowerCase();
    return dmMessages
      .filter(m => !m.isDeleted && m.content?.toLowerCase().includes(q))
      .map(m => m.id);
  }, [dmMessages, msgSearchQuery]);

  const msgSearchCurrentId = msgSearchMatches.length > 0 ? msgSearchMatches[msgSearchIdx] : null;

  useEffect(() => {
    if (msgSearchCurrentId == null) return;
    const el = document.querySelector(`[data-msg-id="${msgSearchCurrentId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [msgSearchCurrentId]);

  useEffect(() => { setMsgSearchIdx(0); }, [msgSearchQuery]);

  useEffect(() => {
    if (!msgSearchOpen) { setMsgSearchQuery(''); setMsgSearchIdx(0); }
  }, [msgSearchOpen]);

  useEffect(() => { setMsgSearchOpen(false); }, [activeDmRoom]);

  // â”€â”€â”€ Render Message â”€â”€â”€
  const renderMessage = (msg: ChatMessage, idx: number, list: ChatMessage[]) => {
    if (msg.isDeleted) return (
      <Box key={msg.id} sx={{ px: 3, py: 0.5 }}>
        <Typography sx={{ color: alpha(isDark ? '#fff' : '#000', 0.3), fontStyle: 'italic', fontSize: '0.82rem', pl: '52px' }}>
          This message was deleted
        </Typography>
      </Box>
    );

    const prevMsg = idx > 0 ? list[idx - 1] : null;
    const showDateSep = !prevMsg || !isSameDay(prevMsg.createdAt, msg.createdAt);
    const isGrouped = !!(prevMsg && !showDateSep && isSameMinuteAndSender(prevMsg, msg) && !prevMsg.isDeleted);
    const isOwnMsg = msg.sender.id === currentUserId;
    const isHovered = hoverMsgId === msg.id;
    const isSearchMatch = msgSearchQuery.trim() && msgSearchMatches.includes(msg.id);
    const isSearchCurrent = msg.id === msgSearchCurrentId;

    return (
      <React.Fragment key={msg.id}>
        {showDateSep && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: borderColor }} />
            <Typography sx={{
              fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary',
              px: 1.5, py: 0.3, borderRadius: 10,
              bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {formatDate(msg.createdAt)}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: borderColor }} />
          </Box>
        )}

        <Box
          data-msg-id={msg.id}
          onMouseEnter={() => setHoverMsgId(msg.id)}
          onMouseLeave={() => setHoverMsgId(null)}
          onContextMenu={(e) => { e.preventDefault(); setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, message: msg }); }}
          sx={{
            display: 'flex', gap: 1, px: 1.5,
            py: isGrouped ? 0.15 : 0.5,
            alignItems: 'flex-end',
            flexDirection: isOwnMsg ? 'row-reverse' : 'row',
            position: 'relative',
            transition: 'background-color 0.2s',
            bgcolor: isSearchCurrent
              ? (isDark ? alpha('#f59e0b', 0.15) : alpha('#f59e0b', 0.12))
              : isSearchMatch
              ? (isDark ? alpha('#f59e0b', 0.06) : alpha('#f59e0b', 0.05))
              : 'transparent',
            '&:hover': { bgcolor: isSearchCurrent
              ? (isDark ? alpha('#f59e0b', 0.2) : alpha('#f59e0b', 0.18))
              : isDark ? alpha('#fff', 0.02) : alpha('#000', 0.01) },
          }}
        >
          {isGrouped ? (
            <Box sx={{ width: isOwnMsg ? 32 : 40, flexShrink: 0 }} />
          ) : (
            <Avatar
              src={msg.sender?.avatarUrl || undefined}
              sx={{
                width: isOwnMsg ? 32 : 40, height: isOwnMsg ? 32 : 40, flexShrink: 0, alignSelf: 'flex-end',
                background: getAvatarGradient(msg.sender?.email || ''),
                fontSize: isOwnMsg ? '0.75rem' : '0.9rem', fontWeight: 700, color: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              {getInitials(msg.sender)}
            </Avatar>
          )}

          <Box sx={{ maxWidth: '68%', display: 'flex', flexDirection: 'column', alignItems: isOwnMsg ? 'flex-end' : 'flex-start' }}>
            {!isOwnMsg && !isGrouped && (
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: accent.main, mb: 0.3, ml: 0.5, lineHeight: 1 }}>
                {getUserDisplayName(msg.sender)}
              </Typography>
            )}
            <Box sx={{
              px: 1.5, py: 0.85,
              borderRadius: isOwnMsg ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              background: isOwnMsg
                ? 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)'
                : otherBubbleBg,
              color: isOwnMsg ? '#fff' : 'text.primary',
              position: 'relative', boxShadow: isOwnMsg ? '0 2px 10px rgba(109,40,217,0.25)' : otherBubbleShadow,
              display: 'inline-block', maxWidth: '100%',
            }}>
              {msg.replyTo && (
                <Box sx={{
                  borderLeft: `3px solid ${isOwnMsg ? '#fff' : accent.main}`,
                  pl: 1.2, mb: 0.75, py: 0.4,
                  bgcolor: isOwnMsg ? 'rgba(255,255,255,0.22)' : alpha(accent.main, 0.08),
                  borderRadius: 1, px: 1,
                }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: isOwnMsg ? '#fff' : accent.main, lineHeight: 1.3 }}>
                    {getUserDisplayName(msg.replyTo.sender)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: isOwnMsg ? 'rgba(255,255,255,0.9)' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300, lineHeight: 1.3 }}>
                    {msg.replyTo.content}
                  </Typography>
                </Box>
              )}
              {msg.messageType === 'text' && (
                <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.55, wordBreak: 'break-word', whiteSpace: 'pre-wrap', color: 'inherit', fontWeight: 400 }}>
                  {msg.content}
                </Typography>
              )}

              {msg.messageType === 'image' && msg.fileUrl && (
                <Box>
                  <Box
                    component="img"
                    src={`${apiUrl}/chat/files/${msg.fileUrl.split('/').pop()}`}
                    sx={{ maxWidth: 280, maxHeight: 200, borderRadius: 2, display: 'block', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } }}
                    onClick={() => window.open(`${apiUrl}/chat/files/${msg.fileUrl!.split('/').pop()}`, '_blank')}
                  />
                  {msg.content && msg.content !== msg.fileName && (
                    <Typography sx={{ mt: 0.75, fontSize: '0.88rem', color: 'inherit' }}>{msg.content}</Typography>
                  )}
                </Box>
              )}



              {msg.messageType === 'file' && (
                <Box
                  onClick={() => window.open(`${apiUrl}/chat/files/${(msg.fileUrl || '').split('/').pop()}`, '_blank')}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', p: 0.5, borderRadius: 1.5, bgcolor: isOwnMsg ? 'rgba(255,255,255,0.12)' : alpha('#000', 0.05), '&:hover': { bgcolor: isOwnMsg ? 'rgba(255,255,255,0.2)' : alpha('#000', 0.1) } }}
                >
                  <FileIcon sx={{ fontSize: 28, color: 'inherit' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {msg.fileName || 'File'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', opacity: 0.75, color: 'inherit' }}>
                      {formatFileSize(msg.fileSize || 0)}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.4 }}>
                <Typography sx={{ fontSize: '0.62rem', opacity: 0.7, color: 'inherit', lineHeight: 1 }}>
                  {formatTime(msg.createdAt)}
                </Typography>
                {msg.isEdited && !msg.isDeleted && (
                  <Typography sx={{ fontSize: '0.6rem', opacity: 0.65, color: 'inherit', lineHeight: 1 }}>
                    (edited)
                  </Typography>
                )}
                {isOwnMsg && (
                  msg.isRead
                    ? <DoneAllIcon sx={{ fontSize: 13, color: '#60a5fa', lineHeight: 1 }} />
                    : (msg.readBy && msg.readBy.length > 0)
                      ? <DoneAllIcon sx={{ fontSize: 13, color: '#60a5fa', lineHeight: 1 }} />
                      : <DoneIcon sx={{ fontSize: 13, opacity: 0.7, color: 'inherit', lineHeight: 1 }} />
                )}
              </Box>
            </Box>

            {msg.reactions && msg.reactions.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {msg.reactions.map(r => (
                  <Box key={r.emoji} onClick={() => handleReaction(msg.id, r.emoji)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.4,
                      px: 0.9, py: 0.3, borderRadius: 10, cursor: 'pointer',
                      bgcolor: r.users.some(u => u.id === currentUserId) ? alpha(accent.main, 0.2) : (isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06)),
                      border: `1px solid ${r.users.some(u => u.id === currentUserId) ? alpha(accent.main, 0.5) : 'transparent'}`,
                      '&:hover': { bgcolor: alpha(accent.main, 0.25) }, transition: 'all 0.15s',
                    }}>
                    <Typography sx={{ fontSize: 13 }}>{r.emoji}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary' }}>{r.count}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {isHovered && (
            <Fade in>
              <Paper elevation={3}
                onMouseEnter={() => setHoverMsgId(msg.id)}
                onMouseLeave={() => setHoverMsgId(null)}
                sx={{
                position: 'absolute', top: -18,
                right: isOwnMsg ? 40 : 'auto', left: isOwnMsg ? 'auto' : 40,
                zIndex: 10, display: 'flex', borderRadius: '10px',
                bgcolor: isDark ? '#1e2130' : '#fff', border: `1px solid ${borderColor}`,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.12)',
                overflow: 'hidden',
              }}>
                {QUICK_REACTIONS.slice(0, 4).map(emoji => (
                  <Tooltip key={emoji} title={emoji}>
                    <IconButton size="small" onClick={() => handleReaction(msg.id, emoji)}
                      sx={{ fontSize: 15, p: 0.6, borderRadius: 0, '&:hover': { bgcolor: hoverBg } }}>{emoji}</IconButton>
                  </Tooltip>
                ))}
                <Box sx={{ width: '1px', bgcolor: borderColor, my: 0.75 }} />
                <Tooltip title="Reply">
                  <IconButton size="small" onClick={() => setReplyTo(msg)}
                    sx={{ p: 0.6, borderRadius: 0, '&:hover': { bgcolor: hoverBg, color: accent.main } }}>
                    <ReplyIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                {isOwnMsg && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => { setEditingMessage(msg); setMessage(msg.content); }}
                      sx={{ p: 0.6, borderRadius: 0, '&:hover': { bgcolor: hoverBg, color: accent.main } }}>
                      <EditIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {isOwnMsg && (
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDeleteMsg(msg.id)}
                      sx={{ p: 0.6, borderRadius: 0, '&:hover': { bgcolor: hoverBg, color: '#ef4444' } }}>
                      <DeleteIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Paper>
            </Fade>
          )}
        </Box>
      </React.Fragment>
    );
  };

  // â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• LAYOUT â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
  return (
    <Box sx={{
      display: 'flex', height: '100%', width: '100%', overflow: 'hidden',
      backgroundColor: mainBg,
      fontFamily: '"Inter", "SF Pro Text", system-ui, -apple-system, sans-serif',
    }}>

      {/* â•â•â• SIDEBAR â•â•â• */}
      <Box sx={{
        width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column',
        bgcolor: sidebarBg, borderRight: `1px solid ${borderColor}`,
      }}>
        {/* Header */}
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', flex: 1, color: 'text.primary' }}>
              Messages
            </Typography>
            <Box
              onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', px: 1, py: 0.4, borderRadius: 2, '&:hover': { bgcolor: hoverBg } }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(currentUserStatus), flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', textTransform: 'capitalize' }}>
                {currentUserStatus === 'available' ? 'Online' : currentUserStatus}
              </Typography>
            </Box>
          </Box>
          <TextField
            fullWidth size="small"
            placeholder="Search conversations..."
            value={sidebarSearch}
            onChange={(e) => setSidebarSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.75 }} />,
              sx: {
                fontSize: '0.83rem', borderRadius: '12px', height: 38,
                bgcolor: isDark ? alpha('#fff', 0.06) : '#fff',
                '& fieldset': { border: `1px solid ${borderColor}` },
                '&:hover fieldset': { borderColor: alpha(accent.main, 0.4) },
                '&.Mui-focused fieldset': { borderColor: accent.main },
              },
            }}
          />
        </Box>

        <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={() => setStatusMenuAnchor(null)}
          PaperProps={{ sx: { mt: 0.5, minWidth: 200, borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' } }}>
          {(['available','busy','away','dnd','brb','invisible'] as const).map(s => (
            <MenuItem key={s} onClick={() => handleStatusChange(s)} sx={{ gap: 1.5, py: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(s), flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: currentUserStatus === s ? 700 : 400 }}>
                {getStatusLabel(s)}
              </Typography>
            </MenuItem>
          ))}
        </Menu>

        {/* Conversation List */}
        <Box sx={{
          flex: 1, overflowY: 'auto', overflowX: 'hidden',
          scrollbarWidth: 'thin', scrollbarColor: `${scrollbarThumb} transparent`,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { background: scrollbarThumb, borderRadius: 2 },
        }}>
          {filteredDmRooms.map(room => {
            const isActive = activeDmRoom?.id === room.id;
            const onlineUser = onlineUsers.find(u => u.id === room.otherUser.id);
            const hasUnread = room.unreadCount > 0;
            const lastMsg = room.lastMessage as any;
            const isOwnLast = lastMsg?.sender?.id === currentUserId;
            const isRead = isOwnLast && lastMsg?.readBy?.some((r: any) => r.id === room.otherUser?.id);
            return (
              <Box
                key={room.id}
                onClick={() => handleOpenDm(room.otherUser.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1,
                  cursor: 'pointer', mx: 0.75, mb: 0.25, borderRadius: '12px',
                  bgcolor: isActive ? (isDark ? alpha(accent.main, 0.07) : '#fefce8') : 'transparent',
                  boxShadow: isActive && !isDark ? '0 2px 12px rgba(0,0,0,0.09)' : 'none',
                  transition: 'background 0.15s, box-shadow 0.15s',
                  '&:hover': { bgcolor: isActive ? (isDark ? alpha(accent.main, 0.09) : '#fefce8') : (isDark ? alpha('#fff', 0.06) : alpha('#fff', 0.8)) },
                }}
              >
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar
                    src={room.otherUser.avatarUrl || undefined}
                    sx={{
                      width: 50, height: 50,
                      background: getAvatarGradient(room.otherUser.email),
                      color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      boxShadow: isActive ? `0 0 0 2px ${accent.main}` : 'none',
                    }}
                  >
                    {getUserDisplayName(room.otherUser).charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{
                    position: 'absolute', bottom: 2, right: 2, width: 12, height: 12, borderRadius: '50%',
                    bgcolor: getStatusColor(onlineUser?.status || 'offline'),
                    border: `2px solid ${isActive ? (isDark ? alpha(accent.main, 0.18) : alpha(accent.main, 0.1)) : sidebarBg}`,
                  }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isActive ? accent.main : 'text.primary' }}>
                      {getUserDisplayName(room.otherUser)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5, flexShrink: 0 }}>
                      {isOwnLast && (
                        <Typography sx={{ fontSize: '0.65rem', color: isRead ? '#0891b2' : alpha(isDark ? '#fff' : '#000', 0.35), fontWeight: 600, lineHeight: 1 }}>{isRead ? '\u2713\u2713' : '\u2713'}</Typography>
                      )}
                      {lastMsg?.createdAt && (
                        <Typography sx={{ fontSize: '0.67rem', color: hasUnread ? accent.main : 'text.disabled', fontWeight: hasUnread ? 700 : 400 }}>
                          {formatRelativeTime(lastMsg.createdAt)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.77rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontWeight: hasUnread ? 600 : 400 }}>
                      {lastMsg
                        ? <>{isOwnLast && <Box component="span" sx={{ fontWeight: 600 }}>You: </Box>}{lastMsg.messageType === 'file' ? '\uD83D\uDCCE File' : lastMsg.messageType === 'voice' ? '\uD83C\uDFB5 Voice' : lastMsg.content}</>
                        : <Box component="span" sx={{ fontStyle: 'italic' }}>Start a conversation</Box>
                      }
                    </Typography>
                    {hasUnread ? (
                      <Box sx={{ ml: 0.75, minWidth: 20, height: 20, borderRadius: 10, bgcolor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, px: 0.5 }}>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                          {room.unreadCount > 9 ? '9+' : room.unreadCount}
                        </Typography>
                      </Box>
                    ) : (
                      <BookmarkIcon sx={{ fontSize: 14, color: '#3b82f6', ml: 0.5, flexShrink: 0, opacity: 0.85 }} />
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}

          {filteredDmRooms.length === 0 && !sidebarSearch && (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>No conversations yet</Typography>
              <Typography sx={{ color: 'text.disabled', fontSize: '0.78rem', mt: 0.5 }}>Start chatting with your team below</Typography>
            </Box>
          )}

          {/* Team members without conversations */}
          {teamUsersWithoutConversations.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Team Members
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{teamUsersWithoutConversations.length}</Typography>
              </Box>
              {teamUsersWithoutConversations.length > 5 && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                  <TextField
                    fullWidth size="small" placeholder="Filter members..."
                    value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.5 }} />, sx: { fontSize: '0.78rem', borderRadius: '10px', height: 32 } }}
                  />
                </Box>
              )}
              {teamUsersWithoutConversations.map(u => (
                <Box key={u.id} onClick={() => handleOpenDm(u.id)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 0.75, cursor: 'pointer', mx: 0.75, borderRadius: '10px', '&:hover': { bgcolor: isDark ? alpha('#fff', 0.06) : '#fff' }, transition: 'background 0.15s' }}>
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar sx={{ width: 40, height: 40, background: getAvatarGradient(u.email), color: '#fff', fontSize: '0.85rem', fontWeight: 700 }}>
                      {getUserDisplayName(u).charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ position: 'absolute', bottom: 1, right: 1, width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(u.status), border: `2px solid ${sidebarBg}` }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.83rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getUserDisplayName(u)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: getStatusColor(u.status), fontWeight: 500 }}>
                      {u.status === 'available' ? 'Online' : u.statusMessage || getStatusLabel(u.status)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* â•â•â• MAIN CHAT â•â•â• */}
      {activeDmRoom ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: mainBg }}>

          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.25,
            bgcolor: isDark ? '#16192a' : '#fff',
            borderBottom: `1px solid ${borderColor}`,
            boxShadow: isDark ? '0 1px 0 rgba(255,255,255,0.05)' : '0 1px 0 rgba(0,0,0,0.06)',
            flexShrink: 0, minHeight: 64,
          }}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={activeDmRoom.otherUser.avatarUrl || undefined}
                sx={{
                  width: 44, height: 44,
                  background: getAvatarGradient(activeDmRoom.otherUser.email),
                  color: '#fff', fontSize: '1rem', fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                {getUserDisplayName(activeDmRoom.otherUser).charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{
                position: 'absolute', bottom: 1, right: 1, width: 12, height: 12, borderRadius: '50%',
                bgcolor: getStatusColor(otherUserInfo?.status || 'offline'),
                border: `2px solid ${isDark ? '#16192a' : '#fff'}`,
              }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                {getUserDisplayName(activeDmRoom.otherUser)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: otherUserInfo?.status === 'available' ? '#10b981' : 'text.disabled', fontWeight: 500, lineHeight: 1.2 }}>
                {otherUserInfo?.status === 'available' ? 'online' : otherUserInfo?.status ? getStatusLabel(otherUserInfo.status) : 'offline'}
                {otherUserInfo?.statusMessage ? ` Â· ${otherUserInfo.statusMessage}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Search messages">
                <IconButton size="small" onClick={() => setMsgSearchOpen(o => !o)} sx={{ p: 1, color: msgSearchOpen ? accent.main : 'text.secondary', bgcolor: msgSearchOpen ? alpha(accent.main, 0.08) : 'transparent', '&:hover': { color: accent.main, bgcolor: alpha(accent.main, 0.08) } }}>
                  <SearchIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="More options">
                <IconButton size="small" onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  sx={{ p: 1, color: Boolean(moreMenuAnchor) ? accent.main : 'text.secondary', bgcolor: Boolean(moreMenuAnchor) ? alpha(accent.main, 0.08) : 'transparent', '&:hover': { color: accent.main, bgcolor: alpha(accent.main, 0.08) } }}>
                  <MoreIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Search bar */}
          {msgSearchOpen && (
            <Box sx={{
              px: 2, py: 1, borderBottom: `1px solid ${borderColor}`,
              bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
              display: 'flex', alignItems: 'center', gap: 1,
              flexShrink: 0,
            }}>
              <TextField
                autoFocus
                size="small"
                placeholder="Search in conversation..."
                value={msgSearchQuery}
                onChange={e => setMsgSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (msgSearchMatches.length === 0) return;
                    setMsgSearchIdx(i => (i + 1) % msgSearchMatches.length);
                  }
                  if (e.key === 'Escape') setMsgSearchOpen(false);
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.75 }} />,
                  sx: { fontSize: '0.85rem', borderRadius: '10px', height: 34 },
                }}
                sx={{ flex: 1 }}
              />
              {msgSearchQuery.trim() && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', minWidth: 60 }}>
                  {msgSearchMatches.length === 0 ? 'No results' : `${msgSearchIdx + 1} / ${msgSearchMatches.length}`}
                </Typography>
              )}
              <Tooltip title="Previous">
                <span>
                  <IconButton size="small" disabled={msgSearchMatches.length === 0}
                    onClick={() => setMsgSearchIdx(i => (i - 1 + msgSearchMatches.length) % msgSearchMatches.length)}
                    sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: accent.main } }}>
                    <ArrowUpIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next">
                <span>
                  <IconButton size="small" disabled={msgSearchMatches.length === 0}
                    onClick={() => setMsgSearchIdx(i => (i + 1) % msgSearchMatches.length)}
                    sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: accent.main } }}>
                    <ArrowDownIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Close search">
                <IconButton size="small" onClick={() => setMsgSearchOpen(false)}
                  sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Three-dots menu */}
          <Menu
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={() => setMoreMenuAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { minWidth: 200, borderRadius: 2, mt: 0.5 } }}
          >
            <MenuItem onClick={() => { messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); setMoreMenuAnchor(null); }}>
              Scroll to oldest
            </MenuItem>
            <MenuItem onClick={() => { navigator.clipboard.writeText(activeDmRoom?.otherUser?.email || ''); setMoreMenuAnchor(null); }}>
              Copy email address
            </MenuItem>
          </Menu>

          {/* Messages area */}
          <Box
            ref={messagesContainerRef}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer.files?.[0];
              if (file) handleFileUpload(file);
            }}
            sx={{
              flex: 1, overflowY: 'auto', py: 1,
              scrollbarWidth: 'thin', scrollbarColor: `${scrollbarThumb} transparent`,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { background: scrollbarThumb, borderRadius: 2 },
              bgcolor: isDark ? '#0f1117' : '#f7f9fc',
              backgroundImage: isDark ? 'none' : 'radial-gradient(circle at 100% 0%, rgba(124,58,237,0.03) 0%, transparent 50%), radial-gradient(circle at 0% 100%, rgba(26,115,232,0.03) 0%, transparent 50%)',
            }}
          >
            {dmMessages.map((msg, idx) => renderMessage(msg, idx, dmMessages))}

            {currentTyping.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.5, alignItems: 'flex-end' }}>
                <Avatar sx={{ width: 40, height: 40, background: getAvatarGradient(activeDmRoom.otherUser.email), color: '#fff', fontSize: '0.9rem', fontWeight: 700 }}>
                  {getUserDisplayName(activeDmRoom.otherUser).charAt(0)}
                </Avatar>
                <Box sx={{ px: 1.75, py: 1, borderRadius: '18px 18px 18px 4px', bgcolor: isDark ? alpha('#fff', 0.09) : '#f0f2f5', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'text.disabled', animation: 'typingBounce 1.4s ease-in-out infinite', animationDelay: `${i * 0.16}s` }} />
                  ))}
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Pasted image preview */}
          {pastedImagePreviewUrl && (
            <Fade in>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.03), borderTop: `1px solid ${borderColor}` }}>
                <Box component="img" src={pastedImagePreviewUrl} sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5, border: `1px solid ${borderColor}` }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Pasted image</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>{pastedImageFile?.name}</Typography>
                </Box>
                <IconButton size="small" onClick={() => { setPastedImageFile(null); if (pastedImagePreviewUrl) { URL.revokeObjectURL(pastedImagePreviewUrl); setPastedImagePreviewUrl(null); } }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Fade>
          )}

          {/* Reply bar */}
          {replyTo && (
            <Fade in>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, bgcolor: isDark ? alpha(accent.main, 0.08) : alpha(accent.main, 0.05), borderTop: `2px solid ${accent.main}` }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: accent.main }}>
                    ↩ {getUserDisplayName(replyTo.sender)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {replyTo.content}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setReplyTo(null)} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Fade>
          )}

          {/* Edit bar */}
          {editingMessage && (
            <Fade in>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, bgcolor: isDark ? alpha('#f59e0b', 0.08) : alpha('#f59e0b', 0.06), borderTop: `2px solid #f59e0b` }}>
                <EditIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b', flex: 1 }}>Editing message</Typography>
                <IconButton size="small" onClick={() => { setEditingMessage(null); setMessage(''); }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Fade>
          )}

          {/* Composer */}
          <Box sx={{
            px: 2, py: 1.25, bgcolor: isDark ? '#16192a' : '#fff',
            borderTop: `1px solid ${borderColor}`,
            display: 'flex', alignItems: 'flex-end', gap: 1,
          }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.currentTarget.value = ''; }}
            />
            <Tooltip title="Attach file">
              <IconButton size="small" onClick={() => fileInputRef.current?.click()}
                sx={{ p: 0.75, color: 'text.secondary', '&:hover': { color: accent.main } }}>
                {uploadingFile ? <CircularProgress size={18} /> : <AttachIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>

            <Box sx={{
              flex: 1, borderRadius: '24px',
              bgcolor: isDark ? alpha('#fff', 0.07) : '#f0f2f5',
              display: 'flex', alignItems: 'flex-end',
              border: '1.5px solid transparent',
              transition: 'border-color 0.2s',
              '&:focus-within': { borderColor: alpha(accent.main, 0.5) },
            }}>
              <TextField
                fullWidth multiline maxRows={8}
                variant="standard"
                placeholder={pastedImagePreviewUrl ? 'Add a caption...' : `Message ${getUserDisplayName(activeDmRoom.otherUser)}...`}
                value={message}
                onChange={(e) => { setMessage(e.target.value); handleTyping(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                onPaste={handlePaste}
                InputProps={{ disableUnderline: true }}
                inputProps={{ 'data-composer-textarea': 'true' } as any}
                sx={{ px: 2, py: 1, '& .MuiInputBase-input': { fontSize: '0.9rem', lineHeight: 1.55 } }}
              />
              <Tooltip title="Emoji">
                <IconButton size="small" onClick={(e) => { setEmojiTarget('input'); setEmojiAnchor(e.currentTarget); }}
                  sx={{ p: 0.75, mr: 0.5, mb: 0.5, color: 'text.secondary', '&:hover': { color: accent.main } }}>
                  <EmojiIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {(message.trim() || pastedImagePreviewUrl) && (
              <Tooltip title="Send">
                <IconButton onClick={handleSendMessage}
                  sx={{ p: 1, bgcolor: accent.main, color: '#fff', flexShrink: 0, '&:hover': { bgcolor: accent.dark }, boxShadow: `0 2px 10px ${alpha(accent.main, 0.4)}` }}>
                  <SendIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      ) : (
        /* ── Empty state ── */
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
          <Box sx={{ fontSize: 64 }}>💬</Box>
          <Typography variant="h6" fontWeight={700}>Your Messages</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Send private messages to your team members
          </Typography>
        </Box>
      )}

      {/* ── Emoji Popover ── */}
      <Popover open={Boolean(emojiAnchor)} anchorEl={emojiAnchor} onClose={() => { setEmojiAnchor(null); setEmojiTarget(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Paper sx={{ p: 1.5, display: 'flex', gap: 0.5 }}>
          {QUICK_REACTIONS.map(emoji => (
            <IconButton key={emoji} size="small" onClick={() => {
              if (emojiTarget === 'input') {
                setMessage(m => m + emoji);
              } else if (emojiTarget && typeof emojiTarget === 'number') {
                handleReaction(emojiTarget, emoji);
              }
              setEmojiAnchor(null); setEmojiTarget(null);
            }} sx={{ fontSize: 22 }}>{emoji}</IconButton>
          ))}
        </Paper>
      </Popover>

      {/* ── Context Menu ── */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {contextMenu?.message && contextMenu.message.sender.id === currentUserId && [
          <MenuItem key="edit" onClick={() => { setEditingMessage(contextMenu.message); setMessage(contextMenu.message.content); setContextMenu(null); }}>
            <EditIcon sx={{ mr: 1, fontSize: 18 }} /> Edit
          </MenuItem>,
          <MenuItem key="del" onClick={() => { handleDeleteMsg(contextMenu.message.id); setContextMenu(null); }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Delete
          </MenuItem>,
        ]}
        <MenuItem onClick={() => { if (contextMenu?.message) setReplyTo(contextMenu.message); setContextMenu(null); }}>
          <ReplyIcon sx={{ mr: 1, fontSize: 18 }} /> Reply
        </MenuItem>
      </Menu>

      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
      `}</style>
    </Box>
  );
}

export default TeamChatPage;
